/* Copyright (c) 2022 MOCA Systems, Inc.  All rights reserved. */

import { UserPreferenceCategories } from '../models/UserPreferenceCategories';

export class FancyFirebaseGetters {
	/**
	 * Get accessCodes/codes path: 'accessCodes/codes/{$code}
	 * @param code code to specific accessCode
	 */
	public getAccessCodesPath(code?: string): string {
		const array = ['accessCodes', 'codes', code].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get account path: 'accounts/{$accountId}
	 * @param accountId accountId to specific account
	 */
	public getAccountsPath(accountId?: string): string {
		const array = ['accounts', accountId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get biEmbeds path: 'biEmbeds
	 */
	public getBiEmbedsPath(): string {
		const array = ['biEmbeds'].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get biEmbedMembers path: 'biEmbedMembers/{$biEmbedId}/{$projectMemberId}'
	 */
	public getBiEmbedMembersPath(biEmbedId: string, projectMemberId?: string): string {
		const array = ['biEmbedMembers', biEmbedId, projectMemberId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get getApiRequestsPath path: 'getApiRequestsPath/{$projectId}/{$jobId}'
	 */
	public getApiRequestsPath(jobId?: string): string {
		const array = ['apiRequests', jobId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get catalogFirebases path: 'catalogFirebases/{$partitionId}
	 * @param projectId projectId to specific project
	 */
	public getCatalogFirebases(partitionId?: string): string {
		const array = ['catalogFirebases', partitionId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get catalogProjects path: 'catalogProjects/{$projectId}
	 * @param projectId projectId to specific project
	 */
	public getCatalogProjectsPath(projectId: string, projectType?: string): string {
		const array = ['catalogProjects', projectId, projectType].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get clientStatusTests path: 'clientStatusTests'
	 */
	public getClientStatusTestsPath(): string {
		return 'clientStatusTests';
	}

	/**
	 * Get constraints path: 'constraints/{$projectId}/{$constraintId}
	 * @param projectId provide to get all constraints from project
	 * @param constraintId provide to get specific constraint
	 */
	public getConstraintsPath(projectId: string, constraintId?: string): string {
		const array = ['constraints', projectId, constraintId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get crewSizes path: 'crewSizes/{$projectId}/{$planId}
	 * @param projectId provide to get crewSizes from project
	 * @param	planId provide to get crewSizes from plan
	 */
	public getCrewSizesPath(projectId: string, planId?: string): string {
		const array = ['crewSizes', projectId, planId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get customFieldDefinition path: 'customFieldDefinition/{$projectId}/definitions/{$customFieldId}
	 * @param projectId provide to get all customFieldDefinitions from project return 'customFieldDefinition/{$projectId}'
	 * @param	customFieldId provide to get specific customFieldDefinition
	 */
	public getCustomFieldDefinitionPath(projectId: string, customFieldId: string): string {
		const array = ['customFieldDefinition', projectId, 'definitions', customFieldId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get customFields path: 'customFields/{$projectId}
	 * @param projectId get customFields from specific project
	 */
	public getCustomFieldsPath(projectId: string): string {
		const array = ['customFields', projectId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get customFieldValueDefinitions path: 'customFieldValueDefinitions/{$projectId}/listOfValuesDefinitions/{$customFieldId}
	 * @param projectId provide to get all customFieldValueDefinitions from project return 'customFieldValueDefinitions/{$projectId}'
	 * @param	customFieldId provide to get specific customFieldValueDefinition
	 */
	public getCustomFieldValueDefinitionsPath(projectId: string, customFieldId: string): string {
		const array = ['customFieldValueDefinitions', projectId, 'listOfValuesDefinitions', customFieldId].filter(
			Boolean
		);
		return array.join('/');
	}

	/**
	 * Get enums path: 'enums'
	 */
	public getEnumsPath(enumType?: string): string {
		const array = ['enums', enumType].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get events path: 'events'
	 */
	public getEventsPath(): string {
		const array = ['events'].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get fbdbRequests path: 'fbdbRequests/{$requestId}
	 * @param requestId get specific requestId
	 */
	public getFbdbRequestsPath(requestId?: string): string {
		const array = ['fbdbRequests', requestId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get fbdbStats path: 'fbdbStats/{$path}
	 * @param path custom path
	 */
	public getFbdbStatsPath(path?: string): string {
		const array = ['fbdbStats', path].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get indices/users path: 'indices/emailTenants/{$emailKey}/${exclusivity}/{$rtdbTenantId}
	 * @param emailKey a "firebase safe" email
	 * @param exclusivity always use exclusive for now
	 * @param rtdbTenantId the rtdb version of the tenantId
	 */
	public getIndicesEmailTenantsPath(
		emailKey: string,
		exclusivity?: 'exclusive' | 'inclusive',
		rtdbTenantId?: string
	): string {
		const array = ['indices', 'emailTenants', emailKey, exclusivity, rtdbTenantId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get indices/users path: 'indices/users/{$userId}/projectMember/{$projectId}
	 * @param userId userId to specific user
	 * @param projectId projectId to specific project
	 */
	public getIndicesUsersPath(userId: string, projectId?: string): string {
		const array = ['indices', 'users', userId, 'projectMember', projectId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get indices/users path: 'indices/users/{$userId}/organizationMember/{$organizationId}
	 * @param userId userId to specific user
	 * @param organizationId organizationId to specific organization
	 */
	public getIndicesUsersOrganizationPath(userId: string, organizationId?: string): string {
		const array = ['indices', 'users', userId, 'organizationMember', organizationId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get indices/users/{$userId}/biEmbeds path: 'indices/users/{$userId}/biEmbeds
	 * @param userId userId to specific user
	 */
	public getIndicesBiEmbedsPath(userId: string): string {
		const array = ['indices', 'users', userId, 'biEmbeds'].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get indices/ticketsTwo path: 'indices/ticketsTwo/{projectId}
	 * @param projectId projectId to specific project
	 */
	public getIndicesTicketsTwoPath(projectId: string): string {
		const array = ['indices', 'ticketsTwo', projectId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get invitedUsers/emails path: 'invitedUsers/emails/{$emailKey}/projects/{$projectId}
	 * @param emailKey emailKey to specific email
	 * @param projectId projectId to specific project
	 */
	public getInvitedUsersEmailsPath(emailKey: string, projectId?: string): string {
		const array = ['invitedUsers', 'emails', emailKey, 'projects', projectId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get invitedUsers/projects path: 'invitedUsers/projects/{$projectId}/{$emailKey}
	 * @param projectId get invitedUsers from specific project
	 * @param emailKey emailKey to specific email
	 */
	public getInvitedUsersProjectsPath(projectId: string, emailKey?: string): string {
		const array = ['invitedUsers', 'projects', projectId, emailKey].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get processingTickets job path: 'jobs/processingTickets/{$projectId}/{$jobId}/{$ticketId}
	 * @param projectId get processingTickets from specific project
	 * @param jobId jobId to specific job
	 * @param ticketId ticketId to specific ticket
	 */
	public getProcessingTicketsJobPath(projectId: string, jobId: string, ticketId?: string): string {
		const array = ['jobs', 'processingTickets', projectId, jobId, ticketId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get locations path: 'locations/{$projectId}/{$locationId}
	 * @param projectId provide to get all locations from project
	 * @param locationId provide to get specific location
	 */
	public getLocationsPath(projectId: string, locationId?: string): string {
		const array = ['locations', projectId, locationId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get organizations path: 'organizations/{$organizationId}
	 * @param organizationId organizationId to specific organization
	 */
	public getOrganizationsPath(organizationId?: string): string {
		const array = ['organizations', organizationId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get organizationMembers path: 'organizationMembers/{$organizationId}/{$userId}
	 * @param organizationId organizationId to specific organization
	 * @param userId user id for the organization member
	 */
	public getOrganizationMembersPath(organizationId: string, userId?: string): string {
		const array = ['organizationMembers', organizationId, userId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get organizationProjects path: 'organizationProjects/{$organizationId}/{$projectId}
	 * @param organizationId organizationId to specific organization
	 * @param projectId projectId to specific project
	 */
	public getOrganizationProjectsPath(organizationId: string, projectId?: string): string {
		const array = ['organizationProjects', organizationId, projectId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get planAccess path: 'planAccess/{$projectId}/{$planId}
	 * @param projectId provide to get planAccess from project
	 * @param	planId provide to get planAccess from plan
	 */
	public getPlanAccessPath(projectId: string, planId?: string): string {
		const array = ['planAccess', projectId, planId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get plans path: 'plans/{$projectId}/{$planId}
	 * @param projectId provide to get all plans from project
	 * @param	planId provide to get specific plan
	 */
	public getPlanPath(projectId: string, planId?: string): string {
		const array = ['plans', projectId, planId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get presence path: 'presence/{$userId}/projects?/{$projectId}
	 * @param userId get presence from specific project
	 */
	public getPresencePath(userId: string, projectId?: string): string {
		let array = ['presence', userId];
		if (projectId) {
			array.push('projects', projectId);
		}
		array = array.filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get projectExceptionDays path: 'projectExceptionDays/{$projectId}
	 * @param projectId get projectExceptionDays from specific project
	 */
	public getProjectExceptionDaysPath(projectId: string): string {
		const array = ['projectExceptionDays', projectId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get projectFiles path: 'projectFiles/{$projectId}
	 * Optional params projectFileType and fileId produce path: 'projectFiles/{$projectId}/{$projectFileType}/{$fileId}
	 *
	 * @param projectId get projectFiles from specific project
	 * @param projectFileType get projectFiles from project file type
	 * @param fileId get specific projectFile
	 */
	public getProjectFilesPath(projectId: string, projectFileType?: string, fileId?: string): string {
		const array = ['projectFiles', projectId, projectFileType, fileId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get projectMemberTicketCustomFields path: 'projectMemberTicketCustomFields/{$projectId}/{$projectMemberId}
	 * @param projectId provide to get all projectMemberTicketCustomFields from project
	 * @param	projectMemberId provide to get specific projectMemberTicketCustomField
	 */
	public getProjectMemberTicketCustomFieldsPath(projectId: string, projectMemberId?: string): string {
		const array = ['projectMemberTicketCustomFields', projectId, projectMemberId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get projectMemberTickets path: 'projectMemberTickets/{$projectId}/{$projectMemberId}
	 * @param projectId provide to get all projectMemberTickets from project
	 * @param	projectMemberId provide to get specific projectMember
	 */
	public getProjectMemberTicketsPath(projectId: string, projectMemberId?: string): string {
		const array = ['projectMemberTickets', projectId, projectMemberId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get projectMembers path: 'projectMembers/{$projectId}/{$projectMemberId}
	 * @param projectId provide to get all projectMembers from project
	 * @param	projectMemberId provide to get specific projectMember
	 */
	public getProjectMembersPath(projectId: string, projectMemberId?: string): string {
		const array = ['projectMembers', projectId, projectMemberId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get projectMetrics path: 'projectMetrics/{$projectId}
	 * @param projectId provide to get all projectMetrics from project
	 */
	public getProjectMetricsPath(projectId: string): string {
		const array = ['projectMetrics', projectId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get projectPresence path: 'projectPresence/{$projectId}
	 * @param projectId projectId to specific projectPresence
	 */
	public getProjectPresencePath(projectId: string): string {
		const array = ['projectPresence', projectId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get projects path: 'projects/{$projectId}
	 * @param projectId projectId to specify project
	 */
	public getProjectsPath(projectId: string): string {
		const array = ['projects', projectId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get projects path: 'projectUserPreferences/{$projectId}/{$userId}/{$category}
	 * Only the fully qualified node is supported (no lists of userPrefs)
	 * @param projectId projectId to specify project
	 * @param userId ...userId
	 * @param category the subcategory of preferences. Very deliberately hardcoded type
	 */
	public getProjectUserPreferencesPath(
		projectId: string,
		userId: string,
		category: UserPreferenceCategories
	): string {
		const array = ['projectUserPreferences', projectId, userId, category].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get roles path: 'roles/{$projectId}
	 * @param projectId get roles from specific project
	 */
	public getRolesPath(projectId: string): string {
		const array = ['roles', projectId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get settings path: 'settings/{$settingName}
	 * @param settingName specific setting
	 */
	public getSettingsPath(settingName?: string): string {
		const array = ['settings', settingName].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get signals path: 'signals/{$path}
	 * @param path custom path
	 */
	public getSignalsPath(path?: string): string {
		const array = ['signals', path].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get snapshotMeta path: 'snapshotMeta/{$projectId}/{$planId}/{$snapshotId}
	 * @param projectId provide to get all snapshotMeta from project
	 * @param	planId provide to get all snapshotMeta from plan
	 * @param snapshotId provide to get specific snapshotMeta
	 */
	public getSnapshotMetaPath(projectId: string, planId?: string, snapshotId?: string): string {
		const array = ['snapshotMeta', projectId, planId, snapshotId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get snapshotPlans path: 'snapshotPlans/{$projectId}/{$planId}/{$snapshotId}
	 * @param projectId provide to get all snapshotPlans from project
	 * @param	planId provide to get all snapshotPlans from plan
	 * @param snapshotId provide to get specific snapshot
	 */
	public getSnapshotPlansPath(projectId: string, planId?: string, snapshotId?: string): string {
		const array = ['snapshotPlans', projectId, planId, snapshotId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get statusMessage path: 'statusMessage'
	 */
	public getStatusMessagePath(): string {
		const array = ['statusMessage'].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get swimlanes path: 'swimlanes/{$projectId}/{$planId}/{$swimlaneId}
	 * @param projectId provide to get all swimlanes from project
	 * @param	planId provide to get all swimlanes from plan
	 * @param swimlaneId provide to get specific swimlane
	 */
	public getSwimlanesPath(projectId: string, planId?: string, swimlaneId?: string): string {
		const array = ['swimlanes', projectId, planId, swimlaneId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get tables path: 'tables/{$projectId}
	 * @param projectId projectId to specific table
	 */
	public getTablesPath(projectId: string): string {
		const array = ['tables', projectId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get projectAttachments path: 'projectAttachments/{$projectId}
	 * @param projectId provide to get all projectAttachments from project
	 */
	public getProjectAttachmentsPath(projectId: string): string {
		const array = ['projectAttachments', projectId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get tenants path: 'tenants/{$tenantId}
	 * @param tenantId provide to get a tenant. Optional only for firebaseTenantId index querying. Grabbing entire list will be blocked
	 */
	public getTenantPath(tenantId?: string): string {
		const array = ['tenants', tenantId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get ticketAttachments path: 'ticketAttachments/{$projectId}/{$planId}/{$ticketId}
	 * @param projectId provide to get all ticketAttachments from project
	 * @param planId provide to get all ticketAttachments from plan
	 * @param ticketId provide to get specific ticket
	 */
	public getTicketAttachmentsPath(projectId: string, planId?: string, ticketId?: string): string {
		const array = ['ticketAttachments', projectId, planId, ticketId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get ticketCustomFields path: 'ticketCustomFields/{$projectId}/{$planId}/{$ticketId}/{$customFieldId}
	 * @param projectId provide to get all ticketCustomFields from project
	 * @param	planId provide to get all ticketCustomFields from plan
	 * @param ticketId provide to get all one ticket CustomFields
	 * @param customFieldId provide to get specific customField
	 */
	public getTicketCustomFieldsPath(
		projectId: string,
		planId?: string,
		ticketId?: string,
		customFieldId?: string
	): string {
		const array = ['ticketCustomFields', projectId, planId, ticketId, customFieldId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get tickets path: 'tickets/{$projectId}/{$planId}/{$ticketId}
	 * @param projectId provide to get all tickets from project
	 * @param	planId provide to get all tickets from plan
	 * @param ticketId provide to get specific ticket
	 */
	public getTicketPath(projectId: string, planId?: string, ticketId?: string): string {
		const array = ['tickets', projectId, planId, ticketId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get getTicketSchedules path: 'ticketSchedules/{$projectId}/{$planId}/{$sessionId}/{$ticketId}
	 * @param sessionId a unique pushId meant to be unique per browser session
	 */
	public getTicketSchedulesPath(
		projectId: string,
		planId: string,
		sessionId: string,
		ticketId?: string
	): string {
		const array = ['ticketSchedules', projectId, planId, sessionId, ticketId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get ticketWorkDays path: 'ticketWorkDays/{$projectId}/{$planId}
	 * @param projectId provide to get all ticketWorkDays from project
	 * @param	planId provide to get all ticketWorkDays from plan
	 * @param	ticketId provide to get from specific ticket
	 */
	public getTicketWorkDaysPath(projectId: string, planId?: string, ticketId?: string): string {
		const array = ['ticketWorkDays', projectId, planId, ticketId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get timelineTickets path: 'timelineTickets/{$projectId}
	 * @param projectId projectId to specify project
	 */
	public getTimelineTicketsPath(projectId: string): string {
		const array = ['timelineTickets', projectId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get userPreferences path: 'userPreferences/{$userId}
	 * @param userId userId to specific user
	 */
	public getUserPreferencesPath(userId?: string): string {
		const array = ['userPreferences', userId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get users path: 'users/{$userId}
	 * @param userId get specific user
	 */
	public getUsersPath(userId?: string): string {
		const array = ['users', userId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get domainTenants path: '/domainTenants/$domainKey
	 * @param domainKey domainKey to specific domain
	 */
	public getDomainTenantsPath(domainKey: string): string {
		const array = ['domainTenants', domainKey].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get Project External Systems Path: 'projectExternalSystems/{$projectId}/{$externalSystemsId}
	 * @param projectId provide to get all external systems from project
	 * @param externalSystemsId provide to get specific external system
	 */
	public getProjectExternalSystemsPath(projectId: string, externalSystemsId?: string): string {
		const array = ['projectExternalSystems', projectId, externalSystemsId].filter(Boolean);
		return array.join('/');
	}

	/**
	 * Get milestone path: 'plans/{$projectId}/{$planId}/milestone`
	 * @param projectId provide to get all plans from project
	 * @param	planId provide to get specific plan
	 */
	public getMilestonePath(projectId: string, planId?: string): string {
		const array = ['plans', projectId, planId, `milestone`].filter(Boolean);
		return array.join('/');
	}
}

export const fancyFirebaseGetters: FancyFirebaseGetters = new FancyFirebaseGetters();
